import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import AuthService from '../services/AuthService';
import '../styles/SessionWarning.css';

const KEEPALIVE_INTERVAL_MS = 4 * 60_000;

const SessionTimeout = ({
  inactivityMins = 115,  // 115 minutes default
  warningSecs = 60,
}) => {
  const { token, logout } = useAuth();

  const logoutTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const warningIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(warningSecs);

  const INACTIVITY_TIMEOUT = inactivityMins * 60_000;
  const WARNING_TIMEOUT = INACTIVITY_TIMEOUT - warningSecs * 1_000;

  const clearAllTimers = useCallback(() => {
    clearTimeout(logoutTimeoutRef.current);
    clearTimeout(warningTimeoutRef.current);
    clearInterval(warningIntervalRef.current);
  }, []);

  const handleLogout = useCallback(() => {
    clearAllTimers();
    logout({
      reason: 'expired',
      message: 'Your session timed out due to inactivity. Please sign in again.',
    });
  }, [logout, clearAllTimers]);

  const startTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    clearAllTimers();
    setIsWarningVisible(false);
    setSecondsLeft(warningSecs);

    // Warning before logout
    warningTimeoutRef.current = setTimeout(() => {
      setIsWarningVisible(true);
      setSecondsLeft(warningSecs);

      warningIntervalRef.current = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 1) {
            clearInterval(warningIntervalRef.current);
          }
          return prev - 1;
        });
      }, 1000);
    }, WARNING_TIMEOUT);

    // Auto logout
    logoutTimeoutRef.current = setTimeout(handleLogout, INACTIVITY_TIMEOUT);
  }, [clearAllTimers, handleLogout, WARNING_TIMEOUT, INACTIVITY_TIMEOUT, warningSecs]);

  const handleActivity = useCallback(() => {
    const inactiveFor = Date.now() - lastActivityRef.current;
    if (inactiveFor >= INACTIVITY_TIMEOUT) {
      handleLogout();
      return;
    }

    startTimers();
  }, [handleLogout, INACTIVITY_TIMEOUT, startTimers]);

  const keepSessionAlive = useCallback(async () => {
    if (!token) return;

    const inactiveFor = Date.now() - lastActivityRef.current;
    if (inactiveFor >= INACTIVITY_TIMEOUT) {
      return;
    }

    try {
      await AuthService.keepAlive();
    } catch (error) {
      if (error?.message !== 'Session expired') {
        console.warn('Session keepalive failed:', error);
      }
    }
  }, [token, INACTIVITY_TIMEOUT]);

  useEffect(() => {
    if (!token) {
      clearAllTimers();
      setIsWarningVisible(false);
      setSecondsLeft(warningSecs);
      return undefined;
    }

    const activityEvents = [
      'click', 'keydown', 'mousemove',
      'scroll', 'touchstart', 'touchmove',
    ];

    const handleReturnToPage = () => {
      if (document.hidden) {
        return;
      }

      const inactiveFor = Date.now() - lastActivityRef.current;
      if (inactiveFor >= INACTIVITY_TIMEOUT) {
        handleLogout();
        return;
      }

      startTimers();
    };

    activityEvents.forEach(event => window.addEventListener(event, handleActivity));
    window.addEventListener('focus', handleReturnToPage);
    window.addEventListener('pageshow', handleReturnToPage);
    document.addEventListener('visibilitychange', handleReturnToPage);
    startTimers(); // Initialize timers on mount
    keepSessionAlive();

    const keepAliveInterval = setInterval(keepSessionAlive, KEEPALIVE_INTERVAL_MS);

    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, handleActivity));
      window.removeEventListener('focus', handleReturnToPage);
      window.removeEventListener('pageshow', handleReturnToPage);
      document.removeEventListener('visibilitychange', handleReturnToPage);
      clearInterval(keepAliveInterval);
      clearAllTimers();
    };
  }, [token, startTimers, clearAllTimers, handleLogout, keepSessionAlive, handleActivity, INACTIVITY_TIMEOUT, warningSecs]);

  return isWarningVisible && (
    <div className="session-warning" role="alert" aria-live="assertive">
      <strong>Session timeout warning</strong>
      <span>
        You will be logged out in <strong>{secondsLeft}</strong> second{secondsLeft !== 1 && 's'}.
        Save your work or interact with the page to stay signed in.
      </span>
    </div>
  );
};

export default SessionTimeout;
